import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import WarehouseRoundedIcon from '@mui/icons-material/WarehouseRounded';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { formatDateTime, formatPrice } from '../../lib/format';
import { computeReadiness } from '../../lib/readiness';
import {
  COMMERCIAL_AVAILABILITY_LABELS,
  STOCK_STATUS_LABELS,
  commercialAvailabilityColor,
  stockStatusColor,
} from '../../model/options';
import type { Product, ProductSummary } from '../../model/types';
import { ReadinessChecklist } from './ReadinessChecklist';

// ── Props ────────────────────────────────────────────────────────────────────

export interface ProductDetailDrawerProps {
  /** Product to display. `null` means the drawer is not mounted. */
  product: ProductSummary | null;
  open: boolean;
  /** Whether the current user has `catalog.manage` permission. */
  canManage: boolean;
  onClose: () => void;
  /** Navigate to the full ProductEditPage for this product. */
  onEdit: (productId: string) => void;
  /** Open the quick-price dialog for this product. */
  onQuickPrice: (product: Product) => void;
  /** Open the quick-stock dialog for this product. */
  onQuickStock: (product: Product) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Right-side product-detail panel that opens on row click from `ProductsPage`.
 *
 * Primary content (above the fold):
 *   - Hero product image
 *   - Readiness checklist (cardStatus · commercialAvailability · published · price)
 *   - Overall readiness verdict alert
 *
 * Secondary content (scroll to see):
 *   - Price / old price
 *   - Category, stock status, channels, updated date
 *
 * Footer actions:
 *   - Quick price edit | Quick stock edit | Open full editor (canManage)
 *   - Open full page (view-only)
 *
 * The drawer does not fetch additional data — it uses the `ProductSummary`
 * already in the page list, making it zero-latency to open.
 */
export function ProductDetailDrawer({
  product,
  open,
  canManage,
  onClose,
  onEdit,
  onQuickPrice,
  onQuickStock,
}: ProductDetailDrawerProps) {
  if (!product) return null;

  const readiness = computeReadiness({
    cardStatus: product.cardStatus,
    commercialAvailability: product.commercialAvailability,
    published: product.published ?? false,
    publishedChannels: product.publishedChannels,
    price: product.price,
  });

  /** Minimal Product shape for quick-edit dialogs (uses only subset of fields). */
  const asProduct: Product = {
    id: product.id,
    name: product.name,
    code: product.code,
    categoryId: product.categoryId ?? '',
    categoryName: product.categoryName ?? '',
    brand: product.brand,
    price: product.price,
    oldPrice: product.oldPrice,
    imageUrl: product.imageUrl,
    stockStatus: (product.stockStatus ?? 'CHECK_AVAILABILITY') as Product['stockStatus'],
    cardStatus: product.cardStatus,
    commercialAvailability: product.commercialAvailability,
  };

  const hasSecondaryContent =
    product.categoryName ||
    product.stockStatus ||
    (product.publishedChannels && product.publishedChannels.length > 0) ||
    product.updatedAt;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100vw', sm: 480 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* ── Header (sticky) ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.35 }}
            noWrap
          >
            {product.name}
          </Typography>
          {(product.code || product.brand) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.25, display: 'block' }}
              noWrap
            >
              {product.code && (
                <Box component="span" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {product.code}
                </Box>
              )}
              {product.code && product.brand ? ' · ' : ''}
              {product.brand}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Закрыть карточку"
          sx={{ flexShrink: 0, mt: -0.25 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Body (scrollable) ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Hero image */}
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16 / 9',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {product.imageUrl ? (
            <Box
              component="img"
              src={product.imageUrl}
              alt={product.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Inventory2RoundedIcon sx={{ fontSize: 56, color: 'action.disabled' }} />
          )}
        </Box>

        {/* ── Readiness panel ─── PRIMARY CONTENT ───────────────────────── */}
        <Stack spacing={1.25} sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
          >
            Готовность к продаже
          </Typography>

          <ReadinessChecklist readiness={readiness} />
        </Stack>

        {/* ── Price ────────────── SECONDARY CONTENT ───────────────────── */}
        <Divider />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2.5, py: 1.75 }}
        >
          <Typography variant="body2" color="text.secondary">
            Цена
          </Typography>
          <Box textAlign="right">
            <Typography variant="body2" fontWeight={700}>
              {formatPrice(product.price)}
            </Typography>
            {product.oldPrice != null && product.oldPrice > (product.price ?? 0) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textDecoration: 'line-through', display: 'block' }}
              >
                {formatPrice(product.oldPrice)}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* ── Details ──────────────────────────────────────────────────── */}
        {hasSecondaryContent && (
          <>
            <Divider />
            <Stack spacing={0} divider={<Divider />}>
              {product.categoryName && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                  sx={{ px: 2.5, py: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Категория
                  </Typography>
                  <Typography variant="body2" sx={{ textAlign: 'right' }}>
                    {product.categoryName}
                  </Typography>
                </Stack>
              )}

              {product.stockStatus && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Наличие
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={stockStatusColor(product.stockStatus)}
                    label={STOCK_STATUS_LABELS[product.stockStatus] ?? product.stockStatus}
                  />
                </Stack>
              )}

              {product.commercialAvailability && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Доступность
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={commercialAvailabilityColor(product.commercialAvailability)}
                    label={
                      COMMERCIAL_AVAILABILITY_LABELS[product.commercialAvailability] ??
                      product.commercialAvailability
                    }
                  />
                </Stack>
              )}

              {product.publishedChannels && product.publishedChannels.length > 0 && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 2.5, py: 1.25 }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                    Каналы
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    justifyContent="flex-end"
                  >
                    {product.publishedChannels.map((ch) => (
                      <Chip key={ch} size="small" variant="outlined" color="success" label={ch} />
                    ))}
                  </Stack>
                </Stack>
              )}

              {product.updatedAt && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ px: 2.5, py: 1.5 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Обновлён
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTime(product.updatedAt)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Box>

      {/* ── Footer actions (sticky) ───────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: (t) => `1px solid ${t.palette.divider}`,
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}
      >
        {canManage ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<LocalOfferRoundedIcon fontSize="small" />}
              onClick={() => onQuickPrice(asProduct)}
              sx={{ flex: 1 }}
            >
              Цена
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<WarehouseRoundedIcon fontSize="small" />}
              onClick={() => onQuickStock(asProduct)}
              sx={{ flex: 1 }}
            >
              Наличие
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<EditRoundedIcon fontSize="small" />}
              onClick={() => onEdit(product.id)}
              sx={{ flex: 2 }}
            >
              Редактировать
            </Button>
          </Stack>
        ) : (
          <Button fullWidth variant="outlined" size="small" onClick={() => onEdit(product.id)}>
            Открыть карточку
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
